// Trial success-page conversion tracking.
//
// Fired exactly once per completed Stripe Checkout Session (keyed by the Stripe
// session id) when the user lands back on the success URL with `?session_id=`.
// Records the Google Ads conversion (AW-17614684437) with enhanced conversions,
// plus a Meta Purchase event that is de-duplicated against the server-side
// Conversions API call via a shared event_id.
//
// All calls no-op safely if a tag failed to load (e.g. blocked by an ad
// blocker) and never throw — the success page must keep working regardless.

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

const AW_CONVERSION_SEND_TO = "AW-17614684437/K9dRCLvRzb0cEJWCq89B";
const CAPI_ENDPOINT = "https://digitaldomination.ai/api/capi";

function gtag(...args: any[]): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

function fbq(...args: any[]): void {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
  }
}

export interface TrialConversionParams {
  sessionId: string;
  email: string | null;
  value?: number;
  currency?: string;
}

// Fire the Google Ads + Meta conversion events for a confirmed trial purchase.
// Guarded by sessionStorage (keyed by the Stripe session id) so a reload or a
// double mount never double-counts.
export function fireTrialConversions({
  sessionId,
  email,
  value = 399.0,
  currency = "USD",
}: TrialConversionParams): void {
  if (typeof window === "undefined" || !sessionId) return;

  const guardKey = `tork_conv_fired_${sessionId}`;
  try {
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");
  } catch {
    // sessionStorage unavailable (e.g. private mode) — fall through and fire.
  }

  // --- Google Ads ---
  // Enhanced conversions: attach the buyer email (gtag hashes it client-side)
  // then record the conversion, de-duplicated by the Stripe session id.
  if (email) gtag("set", "user_data", { email });
  gtag("event", "conversion", {
    send_to: AW_CONVERSION_SEND_TO,
    value,
    currency,
    transaction_id: sessionId,
  });

  // --- Meta Purchase + Conversions API (shared event_id for dedup) ---
  const eid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${sessionId}-${Date.now()}`;

  fbq("track", "Purchase", { value, currency }, { eventID: eid });

  try {
    fetch(CAPI_ENDPOINT, {
      method: "POST",
      mode: "no-cors",
      keepalive: true,
      body: JSON.stringify({
        event_name: "Purchase",
        event_id: eid,
        value,
        currency,
        event_source_url: location.href,
        em: email || undefined,
      }),
    }).catch(() => {
      // Network/CORS failures must never surface on the success page.
    });
  } catch {
    // ignore
  }
}
